<template>
  <i
    v-if="isRelevant"
    class="tm-visual-text v-tm-move-disable"
    :class="classObj"
  >
    <canvas
      ref="display"
      class="-display"
      :style="{ width: `${$data.canvasWidth}px` }"
      @dblclick.stop="$_editTranscript"
    />
  </i>
</template>

<script>
import gridMixin from 'v/Story/Timeline/view/grid.mixin';
import { mapGetters, mapState } from 'vuex';
import {
  getTrackSpanTranscriptionsTokens,
  drawSentencesInMainThread,
} from 'v/Story/Timeline/api/transcription';
import fg from '#/libs/FrameGrid';
import { serialInvocation } from '#/worker/toolkit';
import timemachineTaskManager from 'v/Story/Timeline/api/timemachineTaskManager';
import tmBus from 'v/Story/Timeline/api/tmBus';

export default {
  name: 'tm-visual-text',
  mixins: [gridMixin],

  data() {
    return {
      canvasWidth: 0,
    };
  },

  props: {
    track: { type: Object, required: true },
    rectangle: { type: Object, required: true },
    isFirst: { type: Boolean, required: true },
    isLast: { type: Boolean, required: true },
    videoTranscript: { type: Object, required: false, default: null },
    isSelected: { type: Boolean, required: true },
    isHovered: { type: Boolean, required: true },
  },

  computed: {
    ...mapState('timemachine', {
      pxTranscriptTrackH: (state) => state.cssVariables.pxTranscriptTrackH,
      MODE_AUDIO_RESIZE: (state) => state.MODE.AUDIO_RESIZE,
    }),
    ...mapGetters('timemachine', ['shouldShow_TrackLayerTranscript']),

    isRelevant() {
      return this.hasTranscript && this.shouldShow_TrackLayerTranscript;
    },

    hasTranscript() {
      return (
        !!this.$props.videoTranscript?.tokens?.length &&
        this.$props.track.data.agd.showTranscription
      );
    },

    classObj() {
      const alignRight =
        this.MODE_AUDIO_RESIZE && this.$props.track.resizeEscort.fromLeft;

      return {
        '-align-right': alignRight,
      };
    },

    relevantTokens() {
      const asset = this.$props.track.data;
      const tokens = getTrackSpanTranscriptionsTokens(
        this.$props.videoTranscript?.tokens,
        {
          displayStartTime: asset.displayStartTime,
          displayEndTime: asset.displayEndTime,
          startTime: asset.startTime,
        },
        this.$props.rectangle
      );

      return tokens;
    },

    sentenceMaxDuration() {
      return this.rowDuration <= 10 ? 0 : this.rowDuration / 8;
    },

    isSentenceResolution() {
      // as opposed to token resolution
      return this.sentenceMaxDuration > 0;
    },

    continuum() {
      return this.grid[0].continuum;
    },

    sentences() {
      const { data } = this.$props.track;
      let timeAccumulated = 0;
      const sentences = [];
      let sentenceText = [];
      let startTime = null;
      let endTime = null;
      const lastIndex = this.relevantTokens.length - 1;

      for (let n = 0, N = this.relevantTokens.length; n < N; n++) {
        const token = this.relevantTokens[n];

        if (0 === timeAccumulated) {
          sentenceText = [];
          startTime = token.startTime;
        }
        endTime = token.endTime;
        sentenceText.push(token.content);
        timeAccumulated += token.endTime - token.startTime;

        if (timeAccumulated > this.sentenceMaxDuration || n === lastIndex) {
          const shift = ~~fg.trunc(
            (data.displayStartTime +
              startTime -
              this.$props.rectangle.startTime -
              data.startTime) /
              this.continuum
          );
          const width = ~~fg.trunc(
            Math.min(
              (endTime - startTime) / this.continuum,
              this.$props.rectangle.width - shift
            )
          );

          sentences.push({
            shift,
            width,
            text: sentenceText.join(' '),
          });

          timeAccumulated = 0;
        }
      }

      return sentences;
    },

    renderContextState() {
      if (this.$props.isSelected) {
        return 'SELECTED';
      } else if (this.$props.isHovered) {
        return 'HOVERED';
      } else {
        return 'IDLE';
      }
    },

    canvasColours() {
      if (/HOVERED|SELECTED/.test(this.renderContextState)) {
        return {
          textColour: 'rgb(6,169,244)',
          bgColour: '#f2f2f2',
        };
      } else {
        return {
          textColour: 'rgba(0,0,0, 0.7)',
          bgColour: '#f2f2f2',
        };
      }
    },

    scale() {
      const factor = window.devicePixelRatio ?? 1;
      // is 2 on retina display
      return {
        factor,
        spanWidth: this.$props.rectangle.width,
        width: factor * this.$props.rectangle.width,
        height: factor * this.pxTranscriptTrackH,
        fontSize: factor * 12,
        isSentenceResolution: this.isSentenceResolution,
        paddingRight: 50, // px between sentences
        state: this.renderContextState,
        ...this.canvasColours,
      };
    },

    drawDependency() {
      return `${this.isRelevant ? 1 : 0}${this.scale.state}${
        this.scale.width
      }${_.reduce(
        this.sentences,
        (rv, s) => {
          return `${rv}${s.shift}${s.width}${s.text}`;
        },
        ''
      )}${this.scale.factor}${this.scale.isSentenceResolution}`;
    },
  },

  created() {
    this.$_drawSentencesInWorker = serialInvocation(
      this.$_drawSentencesInWorkerTarget,
      this.$_drawBitmap
    );
  },

  watch: {
    drawDependency: {
      deep: false,
      immediate: true,
      handler: '$_drawIfNeeded',
    },
  },

  methods: {
    $_drawIfNeeded() {
      if (!this._isDestroyed && this.isRelevant) {
        if (this.$refs.display && this.$_drawSentencesInWorker) {
          this.$_displaySentences();
        } else {
          this.$nextTick(this.$_drawIfNeeded); // async recursion
        }
      }
    },

    $_displaySentences() {
      if (window.OffscreenCanvas) {
        this.$_drawSentencesInWorker();
      } else {
        // for example: FF, Safari
        drawSentencesInMainThread(
          this.$refs.display,
          this.scale,
          this.sentences
        );
        this.$data.canvasWidth = this.$props.rectangle.width;
      }
    },

    $_drawSentencesInWorkerTarget() {
      return timemachineTaskManager.renderTranscript({
        scale: this.scale,
        sentences: this.sentences,
      });
    },

    $_drawBitmap({ bitmap, width }) {
      if (!this._isDestroyed && this.$refs.display) {
        const canvas = this.$refs.display;
        const bitmapContext = canvas.getContext('bitmaprenderer');
        bitmapContext.transferFromImageBitmap(bitmap);
        this.$data.canvasWidth = width;
      }
    },

    $_editTranscript: _.debounce(
      function () {
        tmBus.$emit(
          'callbackEditTranscript',
          this.$props.track,
          app.TRANSCRIPT_OPENER.TIMELINE_TEXT
        );
      },
      256,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss">
.tm-visual-span > .tm-visual-text {
  position: absolute;
  pointer-events: none;

  &.-align-right > .-display {
    right: 0;
  }

  > .-display {
    position: absolute;
    bottom: calc(-1 * var(--tm-pxTranscriptTrackH));
    height: var(--tm-pxTranscriptTrackH);
    pointer-events: all;
    cursor: default;
  }
}
</style>
